import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
// tslint:disable-next-line: no-submodule-imports
import { FaTrash } from 'react-icons/fa';

import { DbKey } from '../../../../adl-gen/common/db';
import { I18nContent, Question, Skill } from '../../../../adl-gen/totalinteraction/futurefit/db';
import { LangCode, withI18n, WithI18nProps } from '../../../../internationalization/i18n-hoc';
import { i18next } from '../../../../internationalization/i18next';
import { CloseIcon, EditIcon } from '../../../../ui/common/icons';
/// --- [ NB This is the main DnDContainer class used in the main UI ] ---
///  Not the modified one used by the Admin UI, ie.:
// import { DnDContainer } from "../../drag-and-drop/drag-and-drop-container"
/// --- [ TODO Merge them ] ---
import { DnDContainer, DraggableOption } from '../../../../ui/components/drag-and-drop/drag-and-drop-container';
import { Button } from '../../../../ui/widgets/button/button';
import { CheckItem } from '../../../../ui/widgets/check-item/check-item';
import { MarkdownContent } from '../../../../ui/widgets/markdown-content/markdown-content';
import { RadioItem } from '../../../../ui/widgets/radio-item/radio-item';
import { SelectBox, SelectOption } from '../../../../ui/widgets/select/select';
import { uniqueId, WithId } from '../../../../util/utils';
import { fakeCallback } from '../../../stores/quiz-admin-store';
import { DisplayI18nText } from '../../common/editor';
import { QuestionListEntry } from '../lists/question-list/question-list-view';

const styles = require('../../common/editor.css');
// const fonts = require("../../../../ui/common/fonts.css")
const questionScreenStyles = require('../../../../ui/screens/quiz-question-screen/quiz-question-screen.css');
const buttonStyles = require('../../common/common.css');

interface QuestionAnswerDetailsViewProps extends WithI18nProps {
  question: QuestionListEntry;
  onUpdate(questionId: DbKey<Question>): Promise<void>;
  role: 'client-admin' | 'super';
  /// Skill only defined for skill-based questions, not intros and outros
  skill?: Skill;
}

/**
 * Component to maintain Questions.
 */
@observer
class QuestionAnswerDetailsView0 extends React.Component<QuestionAnswerDetailsViewProps> {
  answers: WithId<I18nContent>[] = [];

  @observable lang: LangCode = LangCode.en;
  deleteAllowed: boolean = false;

  constructor(props: Readonly<QuestionAnswerDetailsViewProps>) {
    super(props);
    const { currentLangCode, question } = props;
    this.lang = currentLangCode();
    this.answers = question.answers.map(answer => ({ id: uniqueId(), value: answer }));
  }

  @action
  changeLang = (opt: SelectOption) => {
    this.lang = opt.value as LangCode;
  };

  LangSelector = observer(() => {
    const { i18nOptions } = this.props;
    const onChange = this.changeLang;
    const options = i18nOptions();
    return (
      <SelectBox
        options={options}
        defaultValue={options.find(option => (option.value as LangCode) === this.lang)}
        onChange={onChange}
        xsSpan={2}
        title={'Language'}
      />
    );
  });

  SkillName = () =>
    this.props.skill
      ? DisplayI18nText({
          field: this.props.skill.name,
          title: 'Skill',
          lang: this.lang,
          xsSpan: 2
        })
      : null;

  renderTopButtons = () => {
    const updateButton = () => {
      return (
        <div className={buttonStyles.buttonList}>
          <Button
            // onClick={async () => this.start("update")}
            onClick={async () => this.props.onUpdate(this.props.question.id)}
            Icon={EditIcon}
            size="small"
            variant="round"
            // disabled={this.editable() === undefined}
          >
            Update
          </Button>
        </div>
      );
    };

    const deleteButton = () => {
      return (
        <div className={buttonStyles.buttonList}>
          <Button
            // onClick={async () => this.start("delete")}
            Icon={FaTrash}
            size="small"
            variant="round"
            // disabled={this.selected.size === 0}
          >
            Delete
          </Button>
        </div>
      );
    };

    return (
      <div className={buttonStyles.buttonListContainer}>
        {this.deleteAllowed && deleteButton()}
        {updateButton()}
      </div>
    );
  };

  /// Annoyingly, we have to pull the guts out of QuizQuestion to avoid rewriting the PageLayout
  /// TODO: refactor this code out of QuizQuestion itself
  renderOrderedAnswersPreview(): JSX.Element {
    const { t } = this.props;
    // Default order on first render
    const draggableAnswers: DraggableOption[] = this.answers.map(ac => {
      return {
        id: ac.id,
        text: t(ac.value)
      };
    });
    return <DnDContainer draggableOptions={draggableAnswers} onMove={fakeCallback('on move')} />;
  }

  renderSelectionAnswersPreview(): JSX.Element[] {
    return this.answers.map(ac => this.renderAnswerContentPreview(ac));
  }

  renderAnswerContentPreview = (answer: WithId<I18nContent>): JSX.Element => {
    const { question, t } = this.props;
    switch (question.scoring.kind) {
      case 'singleResponse': {
        return this.renderSingleAnswerContentPreview(t(answer.value, this.lang), answer.id);
      }
      case 'multiResponse':
      case 'submitted': {
        return this.renderMultiAnswerContentPreview(t(answer.value, this.lang), answer.id);
      }
      default:
        return <div>Answer type not implemented</div>;
    }
  };

  renderSingleAnswerContentPreview = (answerContent: string, i: number): JSX.Element => {
    return (
      <div key={i}>
        <RadioItem size={'xxsmall'} label={answerContent} selected={false} onChange={fakeCallback('on radio select')} />
      </div>
    );
  };

  renderMultiAnswerContentPreview = (answerContent: string, i: number): JSX.Element => {
    return (
      <div key={i}>
        <CheckItem
          size={'xxsmall'}
          label={answerContent}
          selected={false}
          onChange={fakeCallback('on checkItem select')}
        />
      </div>
    );
  };

  /* TODO: if the flex settings in page-container are fixed so they work properly, 
   TODO: detailContainer should not be necessary, and this should fit into a page-row */
  FakeQuizQuestion = () => {
    const { question, t } = this.props;
    return (
      <div className={questionScreenStyles.detailContainer}>
        <div className={questionScreenStyles.content}>
          <div className={questionScreenStyles.exit}>
            <CloseIcon size={'xsmall'} onClick={fakeCallback('on close icon click')} />
          </div>
          <div className={questionScreenStyles.title}>{`Question m of n`}</div>
          <div className={questionScreenStyles.question}>
            <MarkdownContent text={t(question.content, this.lang)} />
          </div>
          <div className={questionScreenStyles.answers}>
            {question.scoring.kind === 'orderedResponse'
              ? this.renderOrderedAnswersPreview()
              : this.renderSelectionAnswersPreview()}
          </div>
          <div className={questionScreenStyles.buttons}>
            <Button
              onClick={fakeCallback('on save and close')}
              variant={'tertiary'}
              className={questionScreenStyles.button}
            >
              {i18next.t('question.save-close', 'Save and Close')}
            </Button>
            <Button onClick={fakeCallback('on next')} disabled={true} className={questionScreenStyles.button}>
              {i18next.t('question.next', 'Next')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  Preview = () => {
    const { question } = this.props;
    return (
      <>
        <div className={styles.page}>
          <div className={styles.body}>
            <div>{this.renderTopButtons()}</div>
            <div className={styles.row}>
              <h4>{question.name}</h4>
              <this.LangSelector />
            </div>
            <div className={styles.row}>
              <this.SkillName />
            </div>
          </div>
        </div>
        <this.FakeQuizQuestion />
      </>
    );
  };

  render() {
    return (
      // {/* TODO aria (or DOM) labels */}
      <this.Preview />
    );
  }
}

export const QuestionAnswerDetailsView = withI18n(QuestionAnswerDetailsView0);



// WEBPACK FOOTER //
// ./src/admin/ui/components/detail-views/question-answer-details-view.tsx